import { useEffect, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { Project } from '@/lib/types/project';

interface ProjectSelectorProps {
  value?: string;
  onChange: (value: string) => void;
  organizationId?: string;
}

export function ProjectSelector({ value, onChange, organizationId }: ProjectSelectorProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('status', 'active')
          .order('name', { ascending: true });

        if (error) throw error;
        setProjects((data || []) as Project[]);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [organizationId]);

  return (
    <Select value={value || 'none'} onValueChange={(val) => onChange(val === 'none' ? '' : val)} disabled={loading}>
      <SelectTrigger>
        <SelectValue placeholder={loading ? 'Loading projects...' : 'Select a project (optional)'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">No Project</SelectItem>
        {projects.map((project) => (
          <SelectItem key={project.id} value={project.id}>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: project.color || '#3B82F6' }}
              />
              <span>
                {project.name}
                {project.project_code && <span className="text-muted-foreground ml-1">({project.project_code})</span>}
              </span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
